import { html, nothing } from "lit";
import { customElement } from "lit/decorators.js";
import { SignalElement, inject } from "ui-common/di";
import type { IBroadcasterDiagnosticsState, QualityRating } from "ui-common/services/broadcaster-diagnostics/types";
import { styles } from "./stream-quality-indicator.css";

@customElement("stream-quality-indicator")
export class StreamQualityIndicator extends SignalElement {
  static override styles = [styles];

  @inject<IBroadcasterDiagnosticsState>("broadcasterDiagnosticsState")
  private diagnosticsState!: IBroadcasterDiagnosticsState;

  private getActiveBars(rating: QualityRating): number {
    switch (rating) {
      case "excellent":
      case "good":
        return 3;
      case "moderate":
        return 2;
      case "poor":
      case "critical":
        return 1;
    }
  }

  private getBarClass(barIndex: number, rating: QualityRating, isWarmingUp: boolean): string {
    if (isWarmingUp) return "bar warmup";
    const activeBars = this.getActiveBars(rating);
    if (barIndex <= activeBars) return `bar ${rating}`;
    return "bar inactive";
  }

  private renderTooltip() {
    const snapshot = this.diagnosticsState.latestSnapshot.get();
    const isWarmingUp = this.diagnosticsState.isWarmingUp.get();

    if (isWarmingUp || !snapshot) {
      return html`<div class="tooltip">Checking...</div>`;
    }

    return html`
      <div class="tooltip">
        <div class="tooltip-row">
          <span class="tooltip-label">Bitrate</span>
          <span class="tooltip-value">${snapshot.videoBitrateSent.toFixed(0)} kbps</span>
        </div>
        <div class="tooltip-row">
          <span class="tooltip-label">FPS</span>
          <span class="tooltip-value">${snapshot.framesPerSecond}</span>
        </div>
        <div class="tooltip-row">
          <span class="tooltip-label">RTT</span>
          <span class="tooltip-value">${snapshot.currentRtt.toFixed(0)} ms</span>
        </div>
        <div class="tooltip-row">
          <span class="tooltip-label">Pkt Loss</span>
          <span class="tooltip-value">${(snapshot.remoteFractionLost * 100).toFixed(1)}%</span>
        </div>
        ${snapshot.qualityLimitationReason && snapshot.qualityLimitationReason !== "none"
          ? html`
              <div class="tooltip-row">
                <span class="tooltip-label">Limited</span>
                <span class="tooltip-value">${snapshot.qualityLimitationReason}</span>
              </div>
            `
          : nothing}
      </div>
    `;
  }

  override render() {
    const rating = this.diagnosticsState.qualityRating.get();
    const isWarmingUp = this.diagnosticsState.isWarmingUp.get();

    return html`
      <div class="bars" tabindex="0" role="meter" aria-label="Stream quality: ${isWarmingUp ? "checking" : rating}">
        <div class="${this.getBarClass(1, rating, isWarmingUp)} bar-1"></div>
        <div class="${this.getBarClass(2, rating, isWarmingUp)} bar-2"></div>
        <div class="${this.getBarClass(3, rating, isWarmingUp)} bar-3"></div>
      </div>
      ${this.renderTooltip()}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "stream-quality-indicator": StreamQualityIndicator;
  }
}
